import { usePanelResize } from "@repo/ui/hooks/usePanelResize";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

const SIDEBAR_MIN_WIDTH_PX = 280;
/** Width the sidebar grows to until it is first resized. */
const SIDEBAR_DEFAULT_WIDTH_PX = 340;
/**
 * Whatever the sidebar is dragged to, leave this much for the settings pane.
 * The sidebar gives way to it down to its minimum width.
 */
const RESERVED_SETTINGS_WIDTH_PX = 400;
/** The `gap-2` between the panes, which the resize handle sits in. */
const GAP_PX = 8;

export interface AssessmentEditorSplitLayoutProps {
  /** The assessment tree. */
  sidebar: ReactNode;
  /** The settings pane of the selected tree item. */
  children: ReactNode;
}

/**
 * The assessment editor's two panes, with a handle between them that resizes
 * the sidebar. It owns the width and takes the panes as props so that a width
 * change, which a drag makes on every frame, re-renders the layout without
 * re-rendering the tree or the settings form.
 */
export const AssessmentEditorSplitLayout: FC<
  AssessmentEditorSplitLayoutProps
> = (props) => {
  const { sidebar, children } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  // Undefined until the user resizes the sidebar, and it sizes itself.
  const [sidebarWidth, setSidebarWidth] = useState<number>();

  // The room the editor has changes without the window resizing, when the
  // copilot panel opens or is resized.
  const [containerWidth, setContainerWidth] = useState<number>();
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Clamp here rather than in CSS: a `max-width` the drag doesn't know about
  // lets the stored width run past what's rendered, detaching the handle from
  // the pointer.
  const maxSidebarWidth =
    containerWidth === undefined
      ? SIDEBAR_DEFAULT_WIDTH_PX
      : Math.max(
          SIDEBAR_MIN_WIDTH_PX,
          // Up to an even split with the settings pane. This is never below
          // the width the sidebar takes by itself, which the grid holds to the
          // same reserve, so a first drag to the right can't shrink it.
          Math.min(
            Math.floor((containerWidth - GAP_PX) / 2),
            containerWidth - GAP_PX - RESERVED_SETTINGS_WIDTH_PX,
          ),
        );
  // Less room can leave the stored width above the new cap.
  const renderedSidebarWidth =
    sidebarWidth === undefined
      ? undefined
      : Math.min(sidebarWidth, maxSidebarWidth);

  const { separatorProps, resizeOverlay } = usePanelResize({
    size: renderedSidebarWidth ?? SIDEBAR_DEFAULT_WIDTH_PX,
    minSize: SIDEBAR_MIN_WIDTH_PX,
    maxSize: maxSidebarWidth,
    onSizeChange: setSidebarWidth,
    edge: "right",
    label: "Resize playbook sidebar",
    measureSize: () => sidebarRef.current?.getBoundingClientRect().width,
  });

  return (
    <div
      ref={containerRef}
      style={{
        gridTemplateColumns: `${
          renderedSidebarWidth === undefined
            ? `minmax(${SIDEBAR_MIN_WIDTH_PX}px, ${SIDEBAR_DEFAULT_WIDTH_PX}px)`
            : `${renderedSidebarWidth}px`
          // The settings pane keeps its reserve for as long as the sidebar has
          // room to give, and takes what is left once it is at its minimum.
        } minmax(min(${RESERVED_SETTINGS_WIDTH_PX}px, calc(100% - ${
          SIDEBAR_MIN_WIDTH_PX + GAP_PX
        }px)), 1fr)`,
      }}
      className="grid h-full gap-2"
    >
      <div ref={sidebarRef} className="relative flex max-h-full min-h-0">
        {sidebar}

        <div
          {...separatorProps}
          className="group absolute inset-y-0 -right-2 z-10 flex w-2 cursor-col-resize items-stretch justify-center outline-none"
          onDoubleClick={(event) => {
            event.preventDefault();
            setSidebarWidth(undefined);
          }}
        >
          <div
            aria-hidden
            className="w-0.5 rounded-full bg-transparent transition-colors group-hover:bg-border-strong group-focus-visible:bg-border-strong"
          />
        </div>
      </div>

      {children}

      {resizeOverlay}
    </div>
  );
};
